import { Fragment, type ReactNode } from 'react';
import GridBackdrop from '@/components/ui/GridBackdrop/GridBackdrop';
import Overline from '@/components/ui/Overline/Overline';
import { formatDate, formatIssueNumber, splitAddress } from '@/lib/format';
import logoMark from '@/assets/brand/kenchiku-chino/logo-mark-blue.svg';
import logoType from '@/assets/brand/kenchiku-chino/logo-type-blue.svg';
import { BRAND_NAME, SERVICE_NAME } from '@/lib/brand';
import styles from './ReportCover.module.css';

type Props = {
  address?: string | null;
  generatedAt?: string | null;
  isPlaceholder?: boolean;
  tagline?: string;
  preview?: boolean;
  /** 左上のブランド表示。省略時は「建築知能」のロゴタイプ。パートナー版はロゴを渡す。 */
  brand?: ReactNode;
  /** 題字。省略時は「立地レポート」。パートナー版は成果物名 (例: 「構造塾」立地レポート)。 */
  title?: string;
  /** 宛名 (施主様名、敬称なし)。B 向け PDF の表紙プレビューで使う。 */
  clientName?: string | null;
  /** 作成者 (企業名)。clientName と同じく B 向け。 */
  issuer?: string | null;
};

// 立地レポート 表紙。白い紙に 1px 罫線、ごく薄い方眼を敷いた「図面の表紙」。
export default function ReportCover({
  address,
  generatedAt,
  isPlaceholder = false,
  tagline = '土地を決める前に、立地をたしかめる。',
  preview = false,
  brand,
  title = '立地レポート',
  clientName,
  issuer,
}: Props) {
  const { pref, name } = splitAddress(address);
  // 題字は「」で括ったブランド名の直後でだけ折り返す (例: 「構造塾」立地レポート → 「構造塾」/ 立地レポート)。
  // 途中で切れると読めない (「構造塾」立地レ/ポート) ので、CSS 側は keep-all で任意の位置の折り返しを止める。
  const titleParts = title.split('」').map((p, i, arr) => (i < arr.length - 1 ? `${p}」` : p)).filter(Boolean);
  return (
    <div
      className={[
        styles.cover,
        isPlaceholder && styles.placeholder,
        preview && styles.preview,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="立地レポート表紙"
    >
      <GridBackdrop variant="dots" fade className={styles.grid} />

      <div className={styles.head}>
        <span className={styles.brand}>
          {brand ?? <img className={styles.brandType} src={logoType} alt={BRAND_NAME} />}
        </span>
      </div>

      <div className={styles.body}>
        <Overline className={styles.eyebrow}>{brand ? 'Report' : SERVICE_NAME}</Overline>
        <div className={styles.title}>
          {titleParts.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && <wbr />}
              {part}
            </Fragment>
          ))}
        </div>
        <div className={styles.sub}>{tagline}</div>
        <div className={styles.addr}>
          <div className={styles.pref}>{pref || '住所が入ります'}</div>
          <div className={styles.name}>{name || '---'}</div>
        </div>
        {(clientName || issuer) && (
          <dl className={styles.addressee}>
            {clientName && (
              <div className={styles.addresseeRow}>
                <dt>施主</dt>
                <dd className={styles.client}>{clientName} 様</dd>
              </div>
            )}
            {issuer && (
              <div className={styles.addresseeRow}>
                <dt>作成</dt>
                <dd>{issuer}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      <div className={styles.foot}>
        <div className={styles.meta}>
          発行 {formatDate(generatedAt)}
          <br />第 {formatIssueNumber(generatedAt)} 号
        </div>
        {brand ? (
          <span className={styles.regMark} aria-hidden="true" />
        ) : (
          <img className={styles.mark} src={logoMark} alt="" />
        )}
      </div>
    </div>
  );
}
